/**
 * The screens' data: what is asked for, what is cached under which key, and
 * the one write that moves a balance before the server has agreed to it.
 *
 * **Query keys are the contract.** Everything a group's screen shows hangs
 * off `['group', groupId, …]`, so one `invalidateQueries` after a write
 * refreshes exactly that group and nothing else, and the home screen's
 * `['balances']` is invalidated alongside it because a group's money is part
 * of a home screen's totals.
 *
 * **The optimistic write is the point of the file.** Filing an expense moves
 * the balances on both screens immediately, from the same `src/lib/money.ts`
 * the server will use, and then reconciles:
 *
 *   1. `onMutate` cancels in-flight reads (so a response from before the
 *      write cannot land on top of the prediction), snapshots the caches it
 *      is about to touch, and applies the draft to them.
 *   2. `onError` puts the snapshots back, exactly. A failed write leaves no
 *      trace of itself on the screen — no half-applied balance, no ghost row.
 *   3. `onSuccess` writes the group's real ledger straight from the response,
 *      which carries it precisely so a write does not need a second round
 *      trip to find out what it did.
 *   4. `onSettled` invalidates, so the authoritative version arrives even if
 *      somebody else was writing at the same time.
 *
 * The prediction and the answer are the same arithmetic, so step 3 normally
 * changes nothing visible — which is what "reconciles" should look like.
 */

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query'
import {
  acceptInvite,
  addPlaceholderMember,
  cancelRecurring,
  clearAdminLimit,
  fetchAdminStats,
  fetchAdminUser,
  fetchAdminUsers,
  setAdminLimit,
  setAdminPlan,
  setAdminState,
  ApiFailure,
  confirmSettlement,
  createGroup,
  createInvite,
  createRecurring,
  createProject,
  createTransaction,
  deleteLibraryReceipt,
  deleteReceipt,
  deleteTransaction,
  fetchActivity,
  fetchGroupActivity,
  fetchAwaitingSettlements,
  fetchBalances,
  fetchGroup,
  fetchGroupBalances,
  fetchGroups,
  fetchInviteLanding,
  fetchInvites,
  fetchMe,
  fetchProject,
  fetchProjectBalances,
  fetchProjectSettlementPlan,
  fetchProjects,
  fetchRecurring,
  fetchRecurringRule,
  fetchReceipt,
  fetchReceiptLibrary,
  fetchReceipts,
  fetchSettlementPlan,
  fetchTransactions,
  fileReceipt,
  moveProjectTransactions,
  newIdempotencyKey,
  rejectSettlement,
  renameProject,
  revokeInvite,
  setProjectArchived,
  skipRecurring,
  transactionRequest,
  updateGroup,
  updateRecurring,
  updateTransaction,
  uploadLooseReceipt,
  uploadReceipt,
  type AccountState,
  type AdminUserQuery,
  type AdminUserResponse,
  type BalancesResponse,
  type CreatedGroupResponse,
  type CreatedTransactionResponse,
  type ExpenseDraft,
  type GroupBalancesResponse,
  type CreatedInviteResponse,
  type GroupResponse,
  type Member,
  type ReceiptFilter,
  type RecurringDraft,
  type TransactionsResponse,
} from './api'
import type { PreparedReceipt } from './lib/receipt-image'
import {
  applyDraftToBalances,
  applyDraftToGroupBalances,
  draftTransaction,
  linesForDraft,
  reconcileGroupBalances,
  withDraftTransaction,
  withoutTransaction,
} from './lib/optimistic'
import { enqueueWrite } from './queue-document'
import type { QueuedWrite } from './lib/queue'
import type { UserId } from './lib/money'

export const keys = {
  balances: ['balances'] as const,
  groups: ['groups'] as const,
  activity: ['activity'] as const,
  /** Who you are and what you are allowed — including the storage quota. */
  me: ['me'] as const,
  group: (groupId: string) => ['group', groupId] as const,
  groupBalances: (groupId: string) => ['group', groupId, 'balances'] as const,
  groupTransactions: (groupId: string) => ['group', groupId, 'transactions'] as const,
  /**
   * The plan is keyed by which question was asked as well as by the group:
   * flipping the switch is a different answer, not a stale one, and caching
   * both means flipping back is instant rather than another round trip.
   */
  groupPlan: (groupId: string, simplify: boolean) =>
    ['group', groupId, 'settlement-plan', simplify ? 'simplified' : 'direct'] as const,
  /** The settlements in this group that are waiting on *your* confirmation. */
  groupAwaiting: (groupId: string) => ['group', groupId, 'settlements', 'awaiting'] as const,
  /**
   * A transaction's receipts hang off its group's key, so one
   * `invalidateQueries({ queryKey: keys.group(id) })` after any write in that
   * group refreshes them along with everything else it might have changed.
   */
  receipts: (groupId: string, transactionId: string) =>
    ['group', groupId, 'receipts', transactionId] as const,
  /**
   * The library, per filter — under the same group key, so one
   * `invalidateQueries` after a receipt is filed, uploaded or removed
   * refreshes every view of the pile rather than only the one being looked at.
   */
  receiptLibrary: (groupId: string, filter: ReceiptFilter, uploader: string | null) =>
    ['group', groupId, 'receipt-library', filter, uploader ?? 'anybody'] as const,
  /** One receipt on its own — what the viewer opens from a cold start. */
  receipt: (groupId: string, receiptId: string) =>
    ['group', groupId, 'receipt', receiptId] as const,
  /** A group's live invite links — under the group key, like everything else. */
  invites: (groupId: string) => ['group', groupId, 'invites'] as const,
  /** A group's own activity feed — under the group key, like everything else. */
  groupActivity: (groupId: string) => ['group', groupId, 'activity'] as const,
  /**
   * A group's recurring rules, and one of them with its history. Under the
   * group key like everything else, which is what makes an occurrence
   * skipped here show up on the group's own screen without this file having
   * to know that screen exists.
   */
  recurring: (groupId: string) => ['group', groupId, 'recurring'] as const,
  recurringRule: (groupId: string, recurringId: string) =>
    ['group', groupId, 'recurring', recurringId] as const,
  /**
   * A group's projects, one of them, and the transactions in one. Under the
   * group key like everything else, so a write anywhere in the group — an
   * expense filed into a project, say — refreshes the counts and the
   * settled badge along with the list.
   */
  projects: (groupId: string) => ['group', groupId, 'projects'] as const,
  project: (groupId: string, projectId: string) =>
    ['group', groupId, 'projects', projectId] as const,
  projectTransactions: (groupId: string, projectId: string) =>
    ['group', groupId, 'projects', projectId, 'transactions'] as const,
  projectBalances: (groupId: string, projectId: string) =>
    ['group', groupId, 'projects', projectId, 'balances'] as const,
  /** Keyed by the question, as `groupPlan` is, for the same reason. */
  projectPlan: (groupId: string, projectId: string, simplify: boolean) =>
    [
      'group',
      groupId,
      'projects',
      projectId,
      'settlement-plan',
      simplify ? 'simplified' : 'direct',
    ] as const,
  /**
   * One link, read before it is followed. It is keyed by the *token*, and it
   * is deliberately not under a group: the person reading it is very often not
   * in that group and quite often not signed in, so there is no group key of
   * theirs for it to hang from.
   */
  inviteLanding: (token: string) => ['invite', token] as const,
  /**
   * The admin console. Under its own root key rather than anywhere near a
   * group's, because nothing a group write invalidates can change what it
   * shows — and, more to the point, nothing it shows comes from a group.
   */
  adminStats: ['admin', 'stats'] as const,
  /**
   * A page of accounts, keyed by the question that was asked. Changing a
   * filter is a different answer rather than a stale one, so going back to a
   * filter is a cache hit and never a refetch of a thousand rows.
   */
  adminUsers: (query: AdminUserQuery) =>
    [
      'admin',
      'users',
      query.q?.trim() ?? '',
      query.status ?? 'any',
      query.plan ?? 'any',
      query.overrides ?? 'any',
    ] as const,
  /** One account in full — the panel behind a row, and what every write returns. */
  adminUser: (userId: string) => ['admin', 'user', userId] as const,
}

export const useBalances = () => useQuery({ queryKey: keys.balances, queryFn: () => fetchBalances() })

export const useGroups = () => useQuery({ queryKey: keys.groups, queryFn: () => fetchGroups() })

export const useActivity = () =>
  useQuery({ queryKey: keys.activity, queryFn: () => fetchActivity() })

/**
 * The limits and the storage reading. It is what lets a screen refuse an
 * upload in the file picker instead of after it — and it is only ever a
 * courtesy: the server takes every one of these numbers again, from the limit
 * resolver, on the request that stores the bytes.
 */
export const useMe = () => useQuery({ queryKey: keys.me, queryFn: () => fetchMe() })

export const useGroup = (groupId: string) =>
  useQuery({ queryKey: keys.group(groupId), queryFn: () => fetchGroup(groupId) })

export const useGroupBalances = (groupId: string) =>
  useQuery({ queryKey: keys.groupBalances(groupId), queryFn: () => fetchGroupBalances(groupId) })

export const useGroupTransactions = (groupId: string) =>
  useQuery({ queryKey: keys.groupTransactions(groupId), queryFn: () => fetchTransactions(groupId) })

/**
 * Who should pay whom, to clear the group.
 *
 * `enabled` is still a parameter, but it no longer means "somebody asked for
 * this": the group screen reads the plan with the rest of the screen, because
 * it is one of the two questions a group gets opened for and it should not
 * cost a tap. What the flag holds it back for is the group itself, whose
 * `simplifyDebts` is half the key below — see `src/screens/Group.tsx`.
 *
 * It used to be fetched only behind the Settle up button, on the grounds that
 * a plan invents payments between people who never transacted and that is a
 * poor thing to show unasked. What that argument was really asking for is the
 * plan to say so, and it does, every time: `describePlanShape` states the
 * count before and after the rerouting under the list. The button was never
 * the part doing that work.
 *
 * `simplify` is passed explicitly rather than left to the group's own setting,
 * and the key carries it: the switch flips the group's cached setting straight
 * away, which changes this key, which fetches the other answer by name. So the
 * list on the screen never depends on whether the `PATCH` that saved the
 * preference has landed yet, and flipping back is a cache hit.
 */
export const useSettlementPlan = (groupId: string, enabled: boolean, simplify: boolean) =>
  useQuery({
    queryKey: keys.groupPlan(groupId, simplify),
    queryFn: () => fetchSettlementPlan(groupId, simplify),
    enabled,
  })

/**
 * The payments somebody says they made to you and you have not confirmed.
 *
 * Asked for with the group's screen rather than only when the settle-up panel
 * is opened, because it is a nudge: its whole job is to be seen by somebody
 * who was not looking for it.
 */
export const useAwaitingSettlements = (groupId: string) =>
  useQuery({
    queryKey: keys.groupAwaiting(groupId),
    queryFn: () => fetchAwaitingSettlements(groupId),
  })

/**
 * Makes a group.
 *
 * The write every account that has never been sent an invite link needs, and
 * the only one on the home screen: every other screen in the app lives under
 * `/groups/:groupId`, so until this has been done once there is nowhere to go.
 *
 * Not optimistic, for the reason `useAddPlaceholder` is not: the id is the
 * server's, and the very next thing that happens is a navigation to it. A
 * guessed id would be a route to a group that does not exist yet.
 *
 * The invalidations are the home screen's two lists — `keys.groups` for the
 * card the button is in, and `keys.balances` because the totals above it are
 * computed over the groups you are in, including the empty one you have just
 * made. `keys.activity` goes with them: the server writes a `group.created`
 * row in the same batch, and the feed is on this screen too.
 *
 * They are on `onSettled` rather than `onSuccess` for the reason every other
 * write here has them there: a request that failed on the way back may still
 * have landed, so "we do not know" is answered by re-reading rather than by
 * assuming nothing happened.
 */
export function useCreateGroup() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (draft: { name: string; currency?: string }): Promise<CreatedGroupResponse> =>
      createGroup(draft, newIdempotencyKey()),

    onSettled() {
      void client.invalidateQueries({ queryKey: keys.groups })
      void client.invalidateQueries({ queryKey: keys.balances })
      void client.invalidateQueries({ queryKey: keys.activity })
    },
  })
}

/**
 * "It landed."
 *
 * Not optimistic, and it does not need to be: nothing about a balance moves,
 * so there is no number on the screen racing the request. What it does is
 * remove a row from the nudge and put a "confirmed" mark on an expense row,
 * and both come from the invalidation below.
 */
export function useConfirmSettlement(groupId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (transactionId: string) =>
      confirmSettlement(groupId, transactionId, newIdempotencyKey()),

    onSettled() {
      void client.invalidateQueries({ queryKey: keys.group(groupId) })
      void client.invalidateQueries({ queryKey: keys.activity })
    },
  })
}

/**
 * Turns this group's debt simplification on or off.
 *
 * It is optimistic on the group itself and on nothing else, which is the whole
 * of what it changes: the setting is a preference about which question the
 * plan answers, and no balance, edge or line moves either way (migration
 * 0013). The plan below it is keyed by the answer being asked for, so it is
 * already on the screen by the time this request goes out.
 */
export function useSetSimplifyDebts(groupId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (simplifyDebts: boolean) =>
      updateGroup(groupId, { simplifyDebts }, newIdempotencyKey()),

    async onMutate(simplifyDebts: boolean) {
      await client.cancelQueries({ queryKey: keys.group(groupId) })
      const previous = client.getQueryData<GroupResponse>(keys.group(groupId))

      client.setQueryData<GroupResponse>(keys.group(groupId), (page) =>
        page ? { ...page, group: { ...page.group, simplifyDebts } } : page,
      )
      return previous
    },

    onError(_error, _simplifyDebts, previous) {
      client.setQueryData(keys.group(groupId), previous)
    },

    onSettled() {
      void client.invalidateQueries({ queryKey: keys.group(groupId) })
      void client.invalidateQueries({ queryKey: keys.groups })
      void client.invalidateQueries({ queryKey: keys.activity })
    },
  })
}

/**
 * Renames a group, moves it to a currency it has no transactions in yet, or
 * archives (or unarchives) it — the owner's three fields on the same PATCH
 * `useSetSimplifyDebts` uses above; `requireOwner` in `server/groups.ts` is
 * what makes them the owner's and not any member's.
 *
 * Not optimistic, for the reason `useConfirmSettlement` gives: these are rare,
 * deliberate edits rather than a number racing a request, and archiving in
 * particular locks every write screen in this group (`People`, `Receipts`,
 * `ReceiptViewer`, `ReceiptUploader` all read `archivedAt`), so it is worth
 * reading back rather than guessed at.
 */
export function useUpdateGroupSettings(groupId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (patch: { name?: string; currency?: string; archived?: boolean }) =>
      updateGroup(groupId, patch, newIdempotencyKey()),

    onSettled() {
      void client.invalidateQueries({ queryKey: keys.group(groupId) })
      void client.invalidateQueries({ queryKey: keys.groups })
      void client.invalidateQueries({ queryKey: keys.balances })
      void client.invalidateQueries({ queryKey: keys.activity })
    },
  })
}

/** What `onMutate` hands to `onError` so a failure can be undone exactly. */
type Rollback = {
  provisionalId: string
  balances: BalancesResponse | undefined
  groupBalances: GroupBalancesResponse | undefined
  transactions: TransactionsResponse | undefined
}

export type FileExpenseArgs = {
  draft: ExpenseDraft
  /**
   * The group's members, in the order `GET /api/groups/:id` returned them —
   * `joined_at`, then `user_id`. The order is not decoration: `splitCents`
   * gives an odd cent to the earliest participant, so predicting the split in
   * any other order predicts the wrong person's cent.
   */
  members: readonly Member[]
  /**
   * A receipt to file onto the expense this writes — "make an expense from
   * this receipt", which is one intention and therefore one mutation.
   *
   * It is here, in the arguments, rather than in an `onSuccess` passed to
   * `mutate`: filing an expense is optimistic, so the sheet closes and
   * unmounts the moment the request goes out, and TanStack does not run a
   * `mutate` callback whose component has gone. The `mutationFn` is a promise
   * the mutation owns and always finishes.
   */
  fileReceiptId?: string
  /**
   * Minted once at the call site rather than inside the mutation, so the same
   * value names three things that all have to agree: the provisional row's
   * id (`pending_${idempotencyKey}`), the `Idempotency-Key` the request is
   * sent with, and — if there is no network to send it on — the id the write
   * carries into `src/queue.ts`. One key, so the row the queue eventually
   * replaces is unmistakably the row the screen is already showing.
   */
  idempotencyKey: string
}

/**
 * The reconciliation a create's answer earns, wherever that answer comes
 * from — the request itself, or `src/queue.ts` telling us a queued one has
 * finally landed. `provisionalId` is the row this replaces, and it is the
 * same id either way: `pending_${idempotencyKey}`, so the row the queue is
 * closing out is unmistakably the row the screen has been showing.
 */
function applyCreatedTransaction(
  client: QueryClient,
  groupId: string,
  provisionalId: string,
  created: CreatedTransactionResponse,
): void {
  // The write's own answer carries the group's whole ledger, so the
  // reconciliation is a read of the response rather than another request.
  client.setQueryData<GroupBalancesResponse>(keys.groupBalances(groupId), (page) =>
    reconcileGroupBalances(page, created.ledger.balances),
  )
  client.setQueryData<TransactionsResponse>(keys.groupTransactions(groupId), (page) => {
    const withoutDraft = withoutTransaction(page, provisionalId)
    if (!withoutDraft) return withoutDraft
    return {
      ...withoutDraft,
      transactions: [{ ...created.transaction, receiptCount: 0 }, ...withoutDraft.transactions],
    }
  })
}

/**
 * What `src/main.tsx` hands to `setOnSent` — the other end of the offline
 * queue. A queued write that finally reaches the server gets exactly the
 * reconciliation an online write gets, because by the time this runs that is
 * what it is: `body` is this same write's `CreatedTransactionResponse`, and
 * `write.id` is the provisional row's id, because they were minted from the
 * same idempotency key.
 *
 * It also invalidates, which the online path leaves to `onSettled` — there is
 * no mutation still running to do that for this one, and the caches this
 * write's own group depends on (balances, activity) need the same refresh a
 * normal write gets once it has actually happened.
 */
export function reconcileQueuedTransaction(client: QueryClient, write: QueuedWrite, body: unknown): void {
  const created = body as CreatedTransactionResponse
  if (!created || created.status !== 'ok' || !created.transaction || !created.ledger) return

  applyCreatedTransaction(client, write.what.groupId, write.id, created)
  void client.invalidateQueries({ queryKey: keys.balances })
  void client.invalidateQueries({ queryKey: keys.group(write.what.groupId) })
  void client.invalidateQueries({ queryKey: keys.activity })
}

/** What `mutationFn` returns: sent for real, or handed to the offline queue. */
type FileExpenseResult =
  | { queued: false; created: CreatedTransactionResponse }
  | { queued: true; provisionalId: string }

/**
 * Files an expense against a group, optimistically.
 *
 * The idempotency key is minted once, at the call site, for the reasons
 * `FileExpenseArgs.idempotencyKey` gives — it is what lets the provisional row,
 * the request, and (if it comes to that) the queued write all agree on one
 * id. It is reused on every retry of *that* draft, which is what makes an
 * optimistic screen safe: the balance has already moved, so a request whose
 * reply was lost must be retryable without filing the expense a second time.
 *
 * **A `fetch` that never reached a server queues instead of failing.** That is
 * the one outcome `onError` must never see: the balances above the sheet have
 * already moved, and rolling them back for "there is no signal in this
 * basement" would be undoing money that is not wrong, only not sent yet. A
 * named refusal from a server that *was* reached — the group is archived, the
 * amount is invalid — is not that, and is left to throw and roll back exactly
 * as it always has. `ApiFailure` is how those two are told apart: it is only
 * ever thrown once a response has actually been read.
 */
export function useFileExpense(groupId: string, myId: UserId) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: async ({
      draft,
      fileReceiptId,
      idempotencyKey,
    }: FileExpenseArgs): Promise<FileExpenseResult> => {
      const provisionalId = `pending_${idempotencyKey}`

      let created: CreatedTransactionResponse
      try {
        created = await createTransaction(groupId, draft, idempotencyKey)
      } catch (err) {
        if (err instanceof ApiFailure) throw err

        const currency = client.getQueryData<GroupBalancesResponse>(keys.groupBalances(groupId))
          ?.group.currency ?? 'USD'
        const { path, body } = transactionRequest(groupId, draft)
        await enqueueWrite({
          id: provisionalId,
          key: idempotencyKey,
          method: 'POST',
          path,
          body,
          what: {
            kind: draft.kind === 'repayment' ? 'settlement' : 'expense',
            description: draft.description,
            amountCents: draft.amountCents,
            currency,
            groupId,
          },
        })
        return { queued: true, provisionalId }
      }

      if (fileReceiptId !== undefined) {
        // The expense is written by this point, and a failure of the *second*
        // request must not be reported as a failure of the first: `onError`
        // would put the balances back, which would be a rollback of money
        // that is genuinely filed. What a failure here leaves is a receipt
        // still in the "not filed yet" pile at the top of the grid, next to a
        // select holding the expense that has just been made — which is a
        // visible state somebody can finish in one tap, rather than an error
        // about something that mostly worked. The same is true, and simpler,
        // when there was no network to try it on at all: the receipt stays
        // unfiled and waits for whoever opens the app to file it by hand.
        try {
          await fileReceipt(groupId, fileReceiptId, created.transaction.id, newIdempotencyKey())
        } catch (err) {
          console.error('the expense was filed; its receipt was not', err)
        }
      }
      return { queued: false, created }
    },

    async onMutate({ draft, members, idempotencyKey }: FileExpenseArgs): Promise<Rollback> {
      await Promise.all([
        client.cancelQueries({ queryKey: keys.balances }),
        client.cancelQueries({ queryKey: keys.group(groupId) }),
      ])

      const rollback: Rollback = {
        provisionalId: `pending_${idempotencyKey}`,
        balances: client.getQueryData<BalancesResponse>(keys.balances),
        groupBalances: client.getQueryData<GroupBalancesResponse>(keys.groupBalances(groupId)),
        transactions: client.getQueryData<TransactionsResponse>(keys.groupTransactions(groupId)),
      }

      const order = members.map((member) => member.userId)
      const names = new Map(members.map((member) => [member.userId, member.displayName]))
      const lines = linesForDraft(draft, order)
      const group = {
        id: groupId,
        name: rollback.groupBalances?.group.name ?? groupId,
      }
      const currency = rollback.groupBalances?.group.currency ?? 'USD'

      client.setQueryData<BalancesResponse>(keys.balances, (page) =>
        applyDraftToBalances(page, myId, group, lines, names),
      )
      client.setQueryData<GroupBalancesResponse>(keys.groupBalances(groupId), (page) =>
        applyDraftToGroupBalances(page, lines),
      )
      client.setQueryData<TransactionsResponse>(keys.groupTransactions(groupId), (page) =>
        withDraftTransaction(
          page,
          draftTransaction(rollback.provisionalId, groupId, currency, draft, lines),
        ),
      )

      return rollback
    },

    onError(_error, _args, rollback) {
      if (!rollback) return
      restore(client, groupId, rollback)
    },

    onSuccess(result, _args, rollback) {
      if (result.queued) {
        // Nothing to reconcile — nothing has happened yet. Only the row's
        // own label changes, from "sending" to "queued", which is the
        // difference between a reply that is merely late and a request that
        // has not gone out.
        client.setQueryData<TransactionsResponse>(keys.groupTransactions(groupId), (page) => {
          if (!page) return page
          return {
            ...page,
            transactions: page.transactions.map((row) =>
              row.id === result.provisionalId ? { ...row, queued: true } : row,
            ),
          }
        })
        return
      }
      applyCreatedTransaction(client, groupId, rollback?.provisionalId ?? '', result.created)
    },

    onSettled(result) {
      // A queued write has not happened. Invalidating now would race its own
      // provisional row against a background refetch of a ledger that has
      // never heard of it — on a flaky connection that refetch can succeed
      // even though the write itself did not, which would erase the row
      // `src/queue.ts` is still holding onto. `reconcileQueuedTransaction`
      // invalidates for real once the write actually lands.
      if (result?.queued) return
      void client.invalidateQueries({ queryKey: keys.balances })
      void client.invalidateQueries({ queryKey: keys.group(groupId) })
      void client.invalidateQueries({ queryKey: keys.activity })
    },
  })
}

/**
 * Corrects one that is already filed.
 *
 * Unlike filing, this is not optimistic, and that is a deliberate asymmetry
 * rather than a gap. An optimistic *create* has one row and one set of deltas
 * to apply and a snapshot to put back if it fails; an optimistic *edit* would
 * have to unwind the old lines and apply the new ones across three caches, and
 * the moment it got that wrong the screen would show a balance that is neither
 * the old one nor the new one. An edit is also something a person is looking
 * at when they press it, so the round trip is a wait rather than an
 * interruption.
 *
 * What it does keep is the reconciliation: `PATCH` answers with the group's
 * whole ledger after the write, so the balances are corrected from the
 * response rather than by a second request, exactly as a create is.
 */
export function useEditExpense(groupId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: ({ transactionId, draft }: EditExpenseArgs) =>
      updateTransaction(groupId, transactionId, draft, newIdempotencyKey()),

    onSuccess(response) {
      client.setQueryData<GroupBalancesResponse>(keys.groupBalances(groupId), (page) =>
        reconcileGroupBalances(page, response.ledger.balances),
      )
      client.setQueryData<TransactionsResponse>(keys.groupTransactions(groupId), (page) => {
        if (!page) return page
        return {
          ...page,
          transactions: page.transactions.map((row) =>
            row.id === response.transaction.id
              ? { ...response.transaction, receiptCount: row.receiptCount }
              : row,
          ),
        }
      })
    },

    onSettled() {
      void client.invalidateQueries({ queryKey: keys.balances })
      void client.invalidateQueries({ queryKey: keys.group(groupId) })
      void client.invalidateQueries({ queryKey: keys.activity })
    },
  })
}

export type EditExpenseArgs = { transactionId: string; draft: ExpenseDraft }

/**
 * Takes a settled payment back.
 *
 * The undo for "Settle up" on the plan, and it is deliberately the
 * ordinary transaction delete rather than anything of its own: a settlement
 * is a `repayment` row written down the same path an expense is, so undoing
 * one is the same soft delete — `deleted_at` set, the lines left alone, a
 * snapshot in the feed — and not a second way for the ledger to move.
 *
 * Not optimistic, for the reason `useEditExpense` is not: the balances it
 * puts back are the ones the settlement moved, and predicting them here would
 * mean unwinding a set of lines across three caches with nothing but a guess
 * to put back if the request were refused. The response carries the group's
 * whole ledger, so the correction is a read of it rather than a round trip.
 *
 * Invalidating `keys.group(groupId)` is what makes the recipient's nudge go
 * with it: `keys.groupAwaiting` is under that prefix, and the server has
 * already stopped listing a deleted settlement in it.
 */
export function useCancelSettlement(groupId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (transactionId: string) =>
      deleteTransaction(groupId, transactionId, newIdempotencyKey()),

    onSuccess(response) {
      client.setQueryData<GroupBalancesResponse>(keys.groupBalances(groupId), (page) =>
        reconcileGroupBalances(page, response.ledger.balances),
      )
      client.setQueryData<TransactionsResponse>(keys.groupTransactions(groupId), (page) =>
        withoutTransaction(page, response.transaction.id),
      )
    },

    onSettled() {
      void client.invalidateQueries({ queryKey: keys.balances })
      void client.invalidateQueries({ queryKey: keys.group(groupId) })
      void client.invalidateQueries({ queryKey: keys.activity })
    },
  })
}

/**
 * "It never arrived."
 *
 * The nudge's second answer, and the only one that moves money: the payment
 * is removed and the balance goes back to where it was before somebody
 * recorded it. That makes it `useCancelSettlement` with a different route and
 * a different sentence in the feed — the server performs the identical soft
 * delete — so it puts the response's ledger back the same way, and is not
 * optimistic for the same reason.
 *
 * `keys.group(groupId)` covers the nudge itself (`keys.groupAwaiting` is
 * under that prefix), which is what makes the question disappear once it has
 * been answered.
 */
export function useRejectSettlement(groupId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (transactionId: string) =>
      rejectSettlement(groupId, transactionId, newIdempotencyKey()),

    onSuccess(response) {
      client.setQueryData<GroupBalancesResponse>(keys.groupBalances(groupId), (page) =>
        reconcileGroupBalances(page, response.ledger.balances),
      )
      client.setQueryData<TransactionsResponse>(keys.groupTransactions(groupId), (page) =>
        withoutTransaction(page, response.transaction.id),
      )
    },

    onSettled() {
      void client.invalidateQueries({ queryKey: keys.balances })
      void client.invalidateQueries({ queryKey: keys.group(groupId) })
      void client.invalidateQueries({ queryKey: keys.activity })
    },
  })
}

// ---------------------------------------------------------------------------
// Receipts
// ---------------------------------------------------------------------------

export const useReceipts = (groupId: string, transactionId: string, enabled: boolean) =>
  useQuery({
    queryKey: keys.receipts(groupId, transactionId),
    queryFn: () => fetchReceipts(groupId, transactionId),
    enabled,
  })

/**
 * Attaches a prepared file.
 *
 * Not optimistic, unlike filing an expense, and for a reason that is the
 * mirror image of that one: an optimistic expense can be *predicted* exactly,
 * because the same `src/lib/money.ts` runs on both ends, but an optimistic
 * receipt would be a `blob:` URL standing in for bytes the server has not
 * accepted yet — and the two things it could turn out to be, "stored" and
 * "refused because you are out of room", look nothing alike. The quota bar
 * next to the button is what makes the wait explicable instead: it already
 * said whether this was going to fit.
 *
 * `keys.me` is invalidated alongside the group, because a stored file has just
 * moved the number the bar is drawn from.
 */
export function useAttachReceipt(groupId: string, transactionId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (prepared: PreparedReceipt) =>
      uploadReceipt(groupId, transactionId, {
        file: prepared.file,
        thumbnail: prepared.thumbnail,
        originalName: prepared.originalName,
      }),

    onSettled() {
      void client.invalidateQueries({ queryKey: keys.receipts(groupId, transactionId) })
      void client.invalidateQueries({ queryKey: keys.groupTransactions(groupId) })
      void client.invalidateQueries({ queryKey: keys.me })
      void client.invalidateQueries({ queryKey: keys.activity })
    },
  })
}

/** Takes one off. Soft on the server; the object waits a week for the sweep. */
export function useRemoveReceipt(groupId: string, transactionId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (receiptId: string) =>
      deleteReceipt(groupId, transactionId, receiptId, newIdempotencyKey()),

    onSettled() {
      void client.invalidateQueries({ queryKey: keys.receipts(groupId, transactionId) })
      void client.invalidateQueries({ queryKey: keys.groupTransactions(groupId) })
      void client.invalidateQueries({ queryKey: keys.me })
      void client.invalidateQueries({ queryKey: keys.activity })
    },
  })
}

// ---------------------------------------------------------------------------
// The library
// ---------------------------------------------------------------------------

/**
 * A group's whole pile, a page at a time.
 *
 * `useInfiniteQuery` rather than one big read, because the list is keyset-paged
 * on the server and pretending otherwise would mean either asking for a
 * capped 200 and quietly hiding the rest, or asking for everything and making
 * a group with two years of receipts pay for all of it to draw one screen.
 * The cursor is the server's own opaque one; nothing here does arithmetic on
 * it.
 */
export function useReceiptLibrary(
  groupId: string,
  filter: ReceiptFilter,
  uploader: string | null,
) {
  return useInfiniteQuery({
    queryKey: keys.receiptLibrary(groupId, filter, uploader),
    queryFn: ({ pageParam }) =>
      fetchReceiptLibrary(groupId, { filter, uploader, cursor: pageParam }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    // A receipt's OCR read finishes *after* the upload's own response —
    // `server/receipt-ocr.ts` is backgrounded so the upload does not wait on
    // a model — so a tile can land on this page still `pending`. Polled only
    // while one of the pages already loaded actually is one, and only the
    // pages already loaded, so this is what turns "Reading the total…" into
    // an answer without anybody having to pull to refresh.
    refetchInterval: (query) => {
      const pages = query.state.data?.pages ?? []
      const stillReading = pages.some((page) =>
        page.receipts.some((row) => row.ocrStatus === 'pending'),
      )
      return stillReading ? 2500 : false
    },
  })
}

/**
 * One group's own feed, a page at a time — `useInfiniteQuery` for the same
 * reason as the receipt library above: it is keyset-paged on the server, and
 * a group with years of history should not have to fetch all of it to draw
 * the first screenful.
 */
export function useGroupActivity(groupId: string) {
  return useInfiniteQuery({
    queryKey: keys.groupActivity(groupId),
    queryFn: ({ pageParam }) => fetchGroupActivity(groupId, { cursor: pageParam }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  })
}

/**
 * One receipt, for a viewer opened at its own URL.
 *
 * Only asked for when the grid is not already holding it: opening a tile costs
 * nothing, and this is the case where somebody arrived by link or by pressing
 * a thumbnail under an expense that is older than the first page.
 */
export const useReceipt = (groupId: string, receiptId: string | null, enabled: boolean) =>
  useQuery({
    queryKey: keys.receipt(groupId, receiptId ?? ''),
    queryFn: () => fetchReceipt(groupId, receiptId!),
    enabled: enabled && receiptId !== null,
    // Same reason as the library's own polling above: a receipt opened here
    // — by link, or a thumbnail from below the first page — can still be
    // `pending` for a moment after the upload that made it.
    refetchInterval: (query) => (query.state.data?.receipt.ocrStatus === 'pending' ? 2500 : false),
  })

/**
 * Puts one up with no expense on it.
 *
 * Not optimistic, exactly like the upload onto an expense and for the same
 * reason: what would stand in for it is a `blob:` URL for bytes the server has
 * not accepted, and "stored" and "you are out of room" look nothing alike. The
 * quota bar beside the button is what makes the wait explicable — it has
 * already said whether this was going to fit.
 */
export function useAttachLooseReceipt(groupId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (prepared: PreparedReceipt) =>
      uploadLooseReceipt(groupId, {
        file: prepared.file,
        thumbnail: prepared.thumbnail,
        originalName: prepared.originalName,
      }),

    onSettled() {
      void client.invalidateQueries({ queryKey: keys.group(groupId) })
      void client.invalidateQueries({ queryKey: keys.me })
      void client.invalidateQueries({ queryKey: keys.activity })
    },
  })
}

/**
 * Files one on an expense, or takes it off with `null`.
 *
 * The group key covers the library, the receipts panel under every row and the
 * transaction list — whose `receiptCount` has just moved on one row or two —
 * so one invalidation is the whole of what this changed.
 */
export function useFileReceipt(groupId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: ({ receiptId, transactionId }: FileReceiptArgs) =>
      fileReceipt(groupId, receiptId, transactionId, newIdempotencyKey()),

    onSettled() {
      void client.invalidateQueries({ queryKey: keys.group(groupId) })
      void client.invalidateQueries({ queryKey: keys.activity })
    },
  })
}

export type FileReceiptArgs = { receiptId: string; transactionId: string | null }

/** Takes one out of the pile, filed or not. Soft; the sweep collects it later. */
export function useRemoveLibraryReceipt(groupId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (receiptId: string) =>
      deleteLibraryReceipt(groupId, receiptId, newIdempotencyKey()),

    onSettled() {
      void client.invalidateQueries({ queryKey: keys.group(groupId) })
      void client.invalidateQueries({ queryKey: keys.me })
      void client.invalidateQueries({ queryKey: keys.activity })
    },
  })
}

function restore(client: QueryClient, groupId: string, rollback: Rollback): void {
  client.setQueryData(keys.balances, rollback.balances)
  client.setQueryData(keys.groupBalances(groupId), rollback.groupBalances)
  client.setQueryData(keys.groupTransactions(groupId), rollback.transactions)
}

// ---------------------------------------------------------------------------
// Recurring expenses
// ---------------------------------------------------------------------------

/**
 * The rules a group has, and the four writes on them.
 *
 * **None of them is optimistic, and none of them should be.** Filing an
 * expense moves a balance the moment it is typed, which is what earns that
 * machinery; a recurring rule moves no balance at all. It is a promise that
 * an expense will be written on a date, by the scheduled Worker
 * (`server/recurring.ts`), and the balances on every other screen are exactly
 * as true a second after this write as they were a second before it. There is
 * nothing here for a prediction to be ahead of.
 *
 * They invalidate `keys.group(groupId)` — which the rules hang under — and
 * `keys.activity`, because every one of them writes an activity row in the
 * same batch. Nothing else: no `keys.balances`, no `keys.groupTransactions`,
 * for the reason above.
 */
export const useRecurring = (groupId: string) =>
  useQuery({ queryKey: keys.recurring(groupId), queryFn: () => fetchRecurring(groupId) })

/**
 * One rule and what it has already done — asked for only when a row is
 * opened, like the settlement plan and for the same reason: it is a second
 * request per rule, and the list above it already says everything a person
 * scanning the screen needs.
 */
export const useRecurringRule = (groupId: string, recurringId: string | null, enabled: boolean) =>
  useQuery({
    queryKey: keys.recurringRule(groupId, recurringId ?? ''),
    queryFn: () => fetchRecurringRule(groupId, recurringId!),
    enabled: enabled && recurringId !== null,
  })

/** What every write below invalidates: the group's rules, and the feed. */
function settleRecurring(client: QueryClient, groupId: string): void {
  void client.invalidateQueries({ queryKey: keys.group(groupId) })
  void client.invalidateQueries({ queryKey: keys.activity })
}

export function useCreateRecurring(groupId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (draft: RecurringDraft) => createRecurring(groupId, draft, newIdempotencyKey()),
    onSettled: () => settleRecurring(client, groupId),
  })
}

export function useUpdateRecurring(groupId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: ({ recurringId, draft }: { recurringId: string; draft: RecurringDraft }) =>
      updateRecurring(groupId, recurringId, draft, newIdempotencyKey()),
    onSettled: () => settleRecurring(client, groupId),
  })
}

/** Cancels the series. Soft — what it already made is ordinary expenses and stays. */
export function useCancelRecurring(groupId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (recurringId: string) =>
      cancelRecurring(groupId, recurringId, newIdempotencyKey()),
    onSettled: () => settleRecurring(client, groupId),
  })
}

/**
 * "Not this month."
 *
 * The date is the caller's rather than this hook's, and it is the one the
 * screen was showing: a rule has exactly one date that has not happened yet,
 * the Worker may have moved it since the list was fetched, and naming it is
 * what turns that race into a refusal that says which date is really next
 * instead of skipping the month after the one somebody meant.
 */
export function useSkipRecurring(groupId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: ({ recurringId, occursOn }: { recurringId: string; occursOn: string }) =>
      skipRecurring(groupId, recurringId, occursOn, newIdempotencyKey()),
    onSettled: () => settleRecurring(client, groupId),
  })
}

// ---------------------------------------------------------------------------
// Projects
// ---------------------------------------------------------------------------

/**
 * Projects, and the writes that make, rename, archive and fill them.
 *
 * None of these is optimistic, for the reason the recurring writes are not:
 * nothing here moves a balance. A project is a label on some of the group's
 * transactions, and putting a transaction in one moves no money anywhere. So
 * every write waits for its answer and then invalidates `keys.group(groupId)`
 * — the project list, every project and its transactions, and the group's
 * own transaction list whose rows carry the chip — plus `keys.activity`,
 * because each of them writes a feed row.
 */
export const useProjects = (groupId: string) =>
  useQuery({ queryKey: keys.projects(groupId), queryFn: () => fetchProjects(groupId) })

export const useProject = (groupId: string, projectId: string) =>
  useQuery({
    queryKey: keys.project(groupId, projectId),
    queryFn: () => fetchProject(groupId, projectId),
  })

export const useProjectTransactions = (groupId: string, projectId: string) =>
  useQuery({
    queryKey: keys.projectTransactions(groupId, projectId),
    queryFn: () => fetchTransactions(groupId, undefined, projectId),
  })

export const useProjectBalances = (groupId: string, projectId: string) =>
  useQuery({
    queryKey: keys.projectBalances(groupId, projectId),
    queryFn: () => fetchProjectBalances(groupId, projectId),
  })

/**
 * Who pays whom to clear one project — from the project's own endpoint, never
 * the group's. Held back, like `useSettlementPlan`, until the group's
 * `simplifyDebts` is known, because it is half the key.
 *
 * A Settle up written from it is `useFileExpense` with a `projectId` on the
 * draft, and that hook invalidates `keys.group(groupId)` — which every key
 * here hangs under — so the project's balances, its plan and its Settled
 * badge are re-read along with the group's.
 */
export const useProjectSettlementPlan = (
  groupId: string,
  projectId: string,
  enabled: boolean,
  simplify: boolean,
) =>
  useQuery({
    queryKey: keys.projectPlan(groupId, projectId, simplify),
    queryFn: () => fetchProjectSettlementPlan(groupId, projectId, simplify),
    enabled,
  })

function settleProjects(client: QueryClient, groupId: string): void {
  void client.invalidateQueries({ queryKey: keys.group(groupId) })
  void client.invalidateQueries({ queryKey: keys.activity })
}

export function useCreateProject(groupId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (project: { name: string; transactionIds: readonly string[] }) =>
      createProject(groupId, project, newIdempotencyKey()),
    onSettled: () => settleProjects(client, groupId),
  })
}

export function useRenameProject(groupId: string, projectId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (name: string) => renameProject(groupId, projectId, name, newIdempotencyKey()),
    onSettled: () => settleProjects(client, groupId),
  })
}

/** `true` archives, `false` unarchives. */
export function useSetProjectArchived(groupId: string, projectId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (archived: boolean) =>
      setProjectArchived(groupId, projectId, archived, newIdempotencyKey()),
    onSettled: () => settleProjects(client, groupId),
  })
}

/** Puts transactions in the project (`attach: true`) or takes them out. */
export function useMoveProjectTransactions(groupId: string, projectId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: ({ transactionIds, attach }: { transactionIds: readonly string[]; attach: boolean }) =>
      moveProjectTransactions(groupId, projectId, transactionIds, attach, newIdempotencyKey()),
    onSettled: () => settleProjects(client, groupId),
  })
}

// ---------------------------------------------------------------------------
// Invites, and claiming a placeholder
// ---------------------------------------------------------------------------

/**
 * Adds somebody with nothing but a name.
 *
 * Not optimistic, unlike filing an expense, and for the reason an edit is not:
 * the row the server makes has an id this side cannot predict, and every later
 * write about that person — a split naming them, an invite link claiming
 * them — needs the real one. A guessed id that has to be swapped out is a
 * worse screen than one that waits for the answer.
 */
export function useAddPlaceholder(groupId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (displayName: string) =>
      addPlaceholderMember(groupId, displayName, newIdempotencyKey()),

    onSettled() {
      void client.invalidateQueries({ queryKey: keys.group(groupId) })
      void client.invalidateQueries({ queryKey: keys.groups })
      void client.invalidateQueries({ queryKey: keys.activity })
    },
  })
}

/** The group's live links. Never the tokens — there are no tokens to list. */
export const useInvites = (groupId: string, enabled: boolean) =>
  useQuery({
    queryKey: keys.invites(groupId),
    queryFn: () => fetchInvites(groupId),
    enabled,
  })

/**
 * Mints one, and hands the token back to the caller.
 *
 * The token is in the *response* and nowhere else, so the screen holds it in
 * component state from here: a refetch of the list cannot recover it, because
 * the server stored only its hash. That is the whole reason this returns what
 * it returns rather than leaving the screen to re-read the list.
 */
export function useCreateInvite(groupId: string) {
  const client = useQueryClient()

  return useMutation<
    CreatedInviteResponse,
    Error,
    { placeholderUserId?: string; email?: string | null; maxUses?: number | null }
  >({
    mutationFn: (options) => createInvite(groupId, options, newIdempotencyKey()),

    onSettled() {
      void client.invalidateQueries({ queryKey: keys.invites(groupId) })
      void client.invalidateQueries({ queryKey: keys.activity })
    },
  })
}

export function useRevokeInvite(groupId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (inviteId: string) => revokeInvite(groupId, inviteId, newIdempotencyKey()),

    onSettled() {
      void client.invalidateQueries({ queryKey: keys.invites(groupId) })
      void client.invalidateQueries({ queryKey: keys.activity })
    },
  })
}

/** What a link is for. Read by a stranger, so it is never `enabled: false`. */
export const useInviteLanding = (token: string) =>
  useQuery({
    queryKey: keys.inviteLanding(token),
    queryFn: () => fetchInviteLanding(token),
    retry: false,
  })

/**
 * Follows it.
 *
 * There is no cache surgery here on purpose. Claiming changes *which account
 * this browser is* — the caller's user id afterwards is the placeholder's —
 * and every cached answer on the client was fetched as somebody else. Patching
 * them one by one would be a long list of ways to leave one behind, so the
 * screen reloads instead, which re-reads the session and everything under it
 * from a clean start. A page load is the cheap, correct answer to "you are now
 * a different person".
 */
export function useAcceptInvite(token: string) {
  return useMutation({
    mutationFn: (options: { acknowledgeMerge?: boolean }) =>
      acceptInvite(token, options, newIdempotencyKey()),
  })
}

// ---------------------------------------------------------------------------
// The admin console
// ---------------------------------------------------------------------------

/** The tiles. One read, and no group's anything in it. */
export const useAdminStats = (enabled: boolean) =>
  useQuery({ queryKey: keys.adminStats, queryFn: () => fetchAdminStats(), enabled })

/**
 * The table.
 *
 * `useInfiniteQuery`, like the receipt library and for the same reason: the
 * list is keyset-paged on the server, and a deployment with a thousand
 * accounts in it is exactly the case where asking for "all of them" is asking
 * the database to read all of them to draw fifty rows. The cursor is the
 * server's own opaque one; nothing here does arithmetic on it.
 */
export function useAdminUsers(query: AdminUserQuery, enabled: boolean) {
  return useInfiniteQuery({
    queryKey: keys.adminUsers(query),
    queryFn: ({ pageParam }) => fetchAdminUsers({ ...query, cursor: pageParam }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled,
  })
}

/** One account, with its overrides, its blocklist and what has been done to it. */
export const useAdminUser = (userId: string | null) =>
  useQuery({
    queryKey: keys.adminUser(userId ?? ''),
    queryFn: () => fetchAdminUser(userId!),
    enabled: userId !== null,
  })

/**
 * The console's writes, all four of them, sharing one cache rule.
 *
 * Every one answers with the whole account afterwards, so the panel's own
 * query is *set* rather than invalidated — the screen shows the server's
 * version of what just happened, with no second round trip and no gap in which
 * it shows the old one. The table and the tiles are invalidated instead: their
 * counts, their filters and their other pages are all now potentially wrong,
 * and none of that can be patched from one account's answer without guessing.
 *
 * There is no optimistic update here, deliberately. An optimistic write earns
 * its complexity when the answer is obvious and the screen would otherwise
 * stall — an expense split four ways. Banning somebody is neither: it is a
 * decision somebody wants to *see* land, and a row that flickered to "Banned"
 * and back would be worse than a spinner.
 */
function useAdminWrite<T>(userId: string, write: (input: T) => Promise<AdminUserResponse>) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: write,
    onSuccess: (answer) => {
      client.setQueryData(keys.adminUser(userId), answer)
      void client.invalidateQueries({ queryKey: keys.adminStats })
      void client.invalidateQueries({ queryKey: ['admin', 'users'] })
    },
  })
}

export const useSetAdminPlan = (userId: string) =>
  useAdminWrite(userId, (patch: { planId: string | null; reason: string }) =>
    setAdminPlan(userId, patch),
  )

export const useSetAdminLimit = (userId: string) =>
  useAdminWrite(
    userId,
    (patch: { limitKey: string; value: number | null; reason: string; expiresAt?: string | null }) =>
      setAdminLimit(userId, patch.limitKey, patch),
  )

export const useClearAdminLimit = (userId: string) =>
  useAdminWrite(userId, (patch: { limitKey: string; reason: string }) =>
    clearAdminLimit(userId, patch.limitKey, patch),
  )

export const useSetAdminState = (userId: string) =>
  useAdminWrite(userId, (patch: { state: AccountState; reason: string }) =>
    setAdminState(userId, patch),
  )
